'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueries } from '@morfi/backend-sdk-v2/client';
import { D6_SCALE } from '@/config/solana';
import { onEvent } from '@/events';
import { getAnalyticsUserId } from '@/lib/analytics';
import { isTradeChartMetric } from '@/lib/analytics/trade-chart-context';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { activeTradeAttempts, recordAttemptEvent, useTradeAttemptJournal } from '@/stores/trade-attempt-journal';
const warnedRecoveryFailures: Set<string> = new Set();
const RECOVERY_WINDOW_MS: number = 60 * 60 * 1000;
export function useRecoverTradeTelemetry(userId) {
  const $ = _c(3);
  const attempts = useTradeAttemptJournal(_temp);
  let t0;
  if ($[0] !== attempts || $[1] !== userId) {
    t0 = {
      queries: Object.entries(attempts).flatMap(t1 => {
        const [traceId, attempt] = t1;
        return userId !== null && attempt.userId === userId && attempt.signature !== undefined && attempt.recoveryStoppedAt === undefined && !activeTradeAttempts.has(traceId) && !["success", "error"].includes(attempt.status) ? [{
          queryKey: ["recover-trade-telemetry", userId, traceId],
          queryFn: () => recoverTradeTelemetry(traceId, userId),
          refetchInterval: 5000,
          retry: false
        }] : [];
      })
    };
    $[0] = attempts;
    $[1] = userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  useQueries(t0);
}
function _temp(state) {
  return state.attempts;
}
export async function recoverTradeTelemetry(traceId: string, userId: string): Promise<boolean> {
  if (getAnalyticsUserId() !== userId) return false;
  const attempt = useTradeAttemptJournal.getState().attempts[traceId];
  if (attempt === undefined || attempt.userId !== userId || attempt.signature === undefined || ['success', 'error'].includes(attempt.status)) return false;
  if (attempt.recoveryStoppedAt !== undefined) return true;
  if (Date.now() - attempt.startedAt >= RECOVERY_WINDOW_MS) {
    // Expiry bounds client RPC work; it cannot establish an on-chain failure.
    useTradeAttemptJournal.setState(state => ({
      attempts: {
        ...state.attempts,
        [traceId]: {
          ...attempt,
          recoveryStoppedAt: Date.now()
        }
      }
    }));
    logger.warn('analytics.trade_recovery_window_expired', {
      traceId,
      network: attempt.network
    });
    return true;
  }
  try {
    const response = await getConnection(attempt.network).getSignatureStatuses([attempt.signature], {
      searchTransactionHistory: true
    });
    const status = response.value[0];
    if (status == null || !['confirmed', 'finalized'].includes(status.confirmationStatus ?? '')) return false;
    const name = status.err === null ? 'trade_success' : 'trade_error';
    const properties = {
      ...attempt.properties,
      traceId,
      network: attempt.network,
      signature: attempt.signature,
      latencyMs: Date.now() - attempt.startedAt,
      recovered: true,
      ...(name === 'trade_error' ? {
        errorCode: 'TX_CONFIRM_FAILED',
        stage: 'confirming',
        morfiError: 'none',
        recoveryAction: 'not_retryable',
        errorClass: 'TxError'
      } : {
        chartMetricAtSubmission: typeof attempt.properties.chartMetricAtSubmission === 'string' && isTradeChartMetric(attempt.properties.chartMetricAtSubmission) ? attempt.properties.chartMetricAtSubmission : 'unknown',
        ...(typeof attempt.properties.platformFeeD6 === 'string' ? {
          platformFeeUsd: Number(attempt.properties.platformFeeD6) / D6_SCALE
        } : {}),
        ...(typeof attempt.properties.tradeNotionalD6 === 'string' ? {
          tradeNotionalUsd: Number(attempt.properties.tradeNotionalD6) / D6_SCALE
        } : {})
      })
    };
    // Identity can change while the RPC request is in flight.
    if (getAnalyticsUserId() !== userId) return false;
    if (!recordAttemptEvent(name, properties, userId)) return false;
    onEvent(name, properties as never);
    logger.info('analytics.trade_outcome_recovered', {
      traceId,
      outcome: name,
      network: attempt.network
    });
    return true;
  } catch {
    if (!warnedRecoveryFailures.has(traceId)) {
      warnedRecoveryFailures.add(traceId);
      logger.warn('analytics.trade_recovery_failed', {
        traceId,
        network: attempt.network
      });
    }
    return false;
  }
}